/**
 * UserMenu
 *
 * Shows current user's name with a sign-out option.
 */

import { useState, useRef, useCallback } from 'react';
import { useAuth, useLogout } from '@/lib/auth.js';

export function UserMenu() {
  const { user } = useAuth();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleLogout = useCallback(() => {
    setOpen(false);
    void logout();
  }, [logout]);

  const handleBlur = useCallback((e: React.FocusEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.relatedTarget)) {
      setOpen(false);
    }
  }, []);

  if (!user) return null;

  const initials = user.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="relative" ref={menuRef} onBlur={handleBlur}>
      <button
        onClick={handleToggle}
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-gray-100"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-medium text-white">
          {initials}
        </span>
        <span className="hidden text-gray-700 sm:inline">{user.name}</span>
      </button>

      {open && (
        <div className="absolute right-0 z-10 mt-1 w-48 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5">
          <div className="border-b px-4 py-2">
            <p className="text-sm font-medium text-gray-900">{user.name}</p>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
